import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../App';

// Mapping of stage code / name to Captain
const RW_CAPTAINS = new Set(['MERCH', 'FAB', 'LOG', 'COMM']);

const STAGE_RANK = {
  'WH DEL / GRN DATE': 423,
  'BCP/ONWARD DISPATCH': 422,
  'EX FACT': 421,
  'FI': 420,
  'GPT': 419,
  'LINE OUT': 418,
  'IN LINE': 417,
  'PCD': 416,
  'INDIA TRIMS IN HOUSE': 415,
  'INDIA TRIMS ETA': 414,
  'INDIA TRIMS ETD': 413,
  'CHINA TRIMS IN HOUSE': 412,
  'CHINA TRIMS ETA': 411,
  'CHINA TRIMS ETD': 410,
  'FAB INHOUSE': 409,
  'FAB ETA': 408,
  'FAB SHIPMENT DOCUMENT SHARING': 407,
  'FAB ETD': 406,
  'PP DATE': 405,
  'FABRIC LC FROM BD TO CHINA': 404,
  'DELIVERY CONFIRMATION AND PRICE CLOSER': 403,
  'GARMENT SALES CONTRACT - BANGALDESH SUPPLIER': 402,
  'GARMENT SALES CONTRACT - BANGLADESH SUPPLIER': 402,
  'ORDER SHARING WITH SBD': 401,
  'ORDER SHARING WITH FACTORY': 401,
  // Fabric routing stages (3_x)
  'FPT': 312,
  'IND PSS APPROVAL': 311,
  'BD PSS': 310,
  'INSPECTION': 309,
  'FINISHING': 308,
  'WEAVING': 307,
  'DYEING': 306,
  'YARN INHOUSE': 305,
  'LAB DIP APPROVAL': 304,
  'HANDLOOM': 303,
  'FAB PI': 302,
  'FAB PO': 301,
  // Pre-Order stages (1_x)
  'PACKING INFO/BARCODE': 105,
  'SIZE RATIO': 104,
  'BOM': 103,
  'SPECS': 102,
  'TECPAC': 101
};

function getStageRank(stageName) {
  const k = String(stageName || '').trim().toUpperCase();
  return STAGE_RANK[k] ?? 200;
}

function getStageCaptain(sortOrder, stageName) {
  const so = String(sortOrder || '');
  const nm = String(stageName || '').trim().toUpperCase();

  // 1. Pre-Order (1_x) -> MERCH (RW Team)
  if (so.startsWith('1_')) return 'MERCH';

  // 2. Fabric Sourcing (3_x)
  if (so.startsWith('3_')) {
    if (nm.includes('APPROVAL') || nm.includes('APPR')) return 'MERCH';
    if (nm.includes('ETD') || nm.includes('ETA') || nm.includes('SHIPMENT DOC') || nm.includes('DOCUMENT')) return 'LOG';
    if (nm.includes('LC') || nm.includes('COMMERCIAL')) return 'COMM';
    if (nm.includes('BD PSS')) return 'SBD';
    return 'FAB'; // FAB PO, PI, HANDLOOM, LABDIP, WEAVING, DYEING, IND PSS, FPT
  }

  // 3. Factory Floor (4_x)
  if (so === '4_1' || nm.includes('ORDER SHARING')) return 'MERCH';
  if (nm.includes('CHINA TRIMS ETD') || nm.includes('CHINA TRIMS ETA') || nm.includes('INDIA TRIMS ETD') || nm.includes('INDIA TRIMS ETA')) return 'LOG';
  if (so === '4_2' || so === '4_3' || nm.includes('CONTRACT') || nm.includes('PRICE CLOSER')) return 'COMM';
  if (nm.includes('IN HOUSE') || nm.includes('INHOUSE') || nm.includes('FAB INHOUSE') || nm.includes('GPT')) return 'FACT';
  if (['PCD', 'IN LINE', 'LINE OUT'].includes(nm) || ['4_16', '4_17', '4_18'].includes(so)) return 'FACT / SYS';
  if (so === '4_20' || nm === 'FI') return 'SBD';
  if (so === '4_21' || nm === 'EX FACT' || so === '4_22' || nm.includes('BCP')) return 'LOG';
  if (so === '4_23' || nm.includes('WH DEL') || nm.includes('INVOICE')) return 'COMM / SYS';

  return 'MERCH';
}

export default function ManualRoutingUpdateView({ setView, Header }) {
  const accent = '#6366f1';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [openTas, setOpenTas] = useState(null);
  const [stageData, setStageData] = useState({});
  const [savingUid, setSavingUid] = useState(null);
  const [captainFilter, setCaptainFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('PENDING'); // default show pending
  const [groupByStage, setGroupByStage] = useState(true); // default grouped stage-wise like shipments
  const [stageSortDir, setStageSortDir] = useState('DESC'); // 'DESC' = Last stage at top, 'ASC' = First stage at top
  const [collapsedStages, setCollapsedStages] = useState({}); // { [stageName]: boolean }

  useEffect(() => {
    setLoading(true);
    api('/orders?type=garment')
      .then(d => {
        const rows = Array.isArray(d) ? d : [];
        setOrders(rows);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const loadStages = tas_no => {
    setStageData(s => ({ ...s, [tas_no]: { ...(s[tas_no] || {}), loading: true } }));
    api(`/orders/${encodeURIComponent(tas_no)}/stages`)
      .then(d => {
        const all = Array.isArray(d) ? d : [];
        // STRICT FILTER:
        // 1. applicable must be 1 (or true)
        // 2. Must have an estimated/target date
        // 3. Exclude 2_x (sampling) and auto-derived DPR stages
        // 4. Must be under RW Captain (MERCH, FAB, LOG, COMM)
        const filtered = all.filter(s => {
          const so = String(s.sort_order || '');
          if (so.startsWith('2_')) return false;

          // Applicable flag check (must be 1 or true)
          const isApp = s.applicable === 1 || s.applicable === true || s.applicable === '1';
          if (!isApp) return false;

          // Estimated date check
          const est = s.target_date || s.planned_date || s.date;
          if (!est || est === '—' || String(est).startsWith('1900-')) return false;

          const cap = getStageCaptain(s.sort_order, s.stage_name);
          return RW_CAPTAINS.has(cap);
        }).sort((a, b) => {
          const skA = parseSortOrder(a.sort_order);
          const skB = parseSortOrder(b.sort_order);
          return skA - skB;
        });

        const initial = filtered.map(s => ({
          ...s,
          captain: getStageCaptain(s.sort_order, s.stage_name),
          inputDate: s.actual_date ? String(s.actual_date).slice(0, 10) : ''
        }));
        setStageData(s => ({ ...s, [tas_no]: { loading: false, stages: initial } }));
      })
      .catch(err => setStageData(s => ({ ...s, [tas_no]: { loading: false, error: err.message || 'failed' } })));
  };

  const parseSortOrder = so => {
    if (!so) return 0;
    const parts = String(so).split('_');
    if (parts.length === 1) return (Number(parts[0]) || 0) * 1000;
    return (Number(parts[0]) || 0) * 1000 + (Number(parts[1]) || 0);
  };

  const handleToggleTas = tas_no => {
    if (openTas === tas_no) {
      setOpenTas(null);
    } else {
      setOpenTas(tas_no);
      if (!stageData[tas_no] || !stageData[tas_no].stages) {
        loadStages(tas_no);
      }
    }
  };

  const setDateInput = (tas_no, uniqueid, dateVal) => {
    setStageData(s => {
      const cur = s[tas_no] || { stages: [] };
      const stages = cur.stages.map(st => {
        const uid = st.uniqueid || st.id;
        if (uid === uniqueid) {
          return { ...st, inputDate: dateVal };
        }
        return st;
      });
      return { ...s, [tas_no]: { ...cur, stages } };
    });
  };

  const saveStage = async (tas_no, stage) => {
    const uid = stage.uniqueid || stage.id;
    if (!stage.inputDate) {
      alert('Please select an actual date first');
      return;
    }
    const [y, m, d] = stage.inputDate.split('-');
    const formattedDate = `${d}/${m}/${y}`;
    setSavingUid(uid);

    try {
      const res = await api('/stages/update', {
        method: 'POST',
        body: JSON.stringify({
          uniqueid: uid,
          actual_date: formattedDate,
          remark: '',
          source: 'garment'
        })
      });

      if (res && (res.success || res.message)) {
        setStageData(s => {
          const cur = s[tas_no] || { stages: [] };
          const stages = cur.stages.map(st => {
            const curUid = st.uniqueid || st.id;
            if (curUid === uid) {
              return { ...st, actual_date: stage.inputDate, status: 'done' };
            }
            return st;
          });
          return { ...s, [tas_no]: { ...cur, stages, savedMsg: `Saved ${stage.stage_name}!` } };
        });
        setTimeout(() => {
          setStageData(s => {
            const cur = s[tas_no];
            if (!cur) return s;
            return { ...s, [tas_no]: { ...cur, savedMsg: null } };
          });
        }, 3000);
      } else {
        alert(res?.error || 'Failed to save stage date');
      }
    } catch (e) {
      alert('Error updating stage date');
    } finally {
      setSavingUid(null);
    }
  };

  const filteredOrders = useMemo(() => {
    if (!orders) return [];
    if (!searchQ) return orders;
    const q = searchQ.toLowerCase();
    return orders.filter(o => {
      return (o.tas_no && o.tas_no.toLowerCase().includes(q)) ||
             (o.buyer && o.buyer.toLowerCase().includes(q)) ||
             (o.factory && o.factory.toLowerCase().includes(q)) ||
             (o.merchandiser && o.merchandiser.toLowerCase().includes(q)) ||
             (o.style_name && o.style_name.toLowerCase().includes(q));
    });
  }, [orders, searchQ]);

  // Stage Grouping of Orders (similar to shipments view)
  const stageGroups = useMemo(() => {
    const map = new Map();
    filteredOrders.forEach(o => {
      const st = String(o.current_pending_stage || o.current_stage || 'NO STAGE').trim() || 'NO STAGE';
      let g = map.get(st);
      if (!g) {
        g = { stage: st, rank: getStageRank(st), orders: [], totalQty: 0 };
        map.set(st, g);
      }
      g.orders.push(o);
      g.totalQty += Number(o.orderqty || o.qty || 0);
    });

    const arr = Array.from(map.values());
    arr.sort((a, b) => {
      if (stageSortDir === 'DESC') {
        return (b.rank - a.rank) || a.stage.localeCompare(b.stage);
      } else {
        return (a.rank - b.rank) || a.stage.localeCompare(b.stage);
      }
    });
    return arr;
  }, [filteredOrders, stageSortDir]);

  const toggleStageCollapse = stName => {
    setCollapsedStages(c => ({ ...c, [stName]: !c[stName] }));
  };

  const fmtD = d => {
    if (!d) return '—';
    const s = String(d).slice(0, 10).split('-');
    return s.length === 3 ? `${s[2]}-${s[1]}-${s[0].slice(2)}` : d;
  };

  const CAPTAIN_BADGES = {
    MERCH: { bg: 'rgba(59, 130, 246, 0.15)', text: '#3b82f6', label: 'MERCH' },
    FAB: { bg: 'rgba(16, 185, 129, 0.15)', text: '#10b981', label: 'FAB' },
    LOG: { bg: 'rgba(139, 92, 246, 0.15)', text: '#8b5cf6', label: 'LOG' },
    COMM: { bg: 'rgba(236, 72, 153, 0.15)', text: '#ec4899', label: 'COMM' }
  };

  // Helper to render an individual TAS card
  const renderOrderCard = o => {
    const isOpen = openTas === o.tas_no;
    const tData = stageData[o.tas_no];

    // Filter stages by Captain & Status
    const visibleStages = (tData?.stages || []).filter(st => {
      if (captainFilter !== 'ALL' && st.captain !== captainFilter) return false;
      const isDone = st.status === 'done' || !!st.actual_date;
      if (statusFilter === 'PENDING' && isDone) return false;
      if (statusFilter === 'DONE' && !isDone) return false;
      return true;
    });

    return (
      <div
        key={o.tas_no}
        style={{
          marginBottom: 10, borderRadius: 10, border: '1px solid var(--border)',
          background: 'var(--bg2)', overflow: 'hidden', boxShadow: isOpen ? '0 4px 16px rgba(0,0,0,0.15)' : 'none'
        }}
      >
        {/* TAS Header Card */}
        <div
          onClick={() => handleToggleTas(o.tas_no)}
          style={{
            padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
            background: isOpen ? 'var(--bg3)' : 'var(--bg2)', borderBottom: isOpen ? '1px solid var(--border)' : 'none'
          }}
        >
          <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 15, color: accent, minWidth: 90 }}>
            {o.tas_no}
          </div>
          <div style={{ flex: 1, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
            <div><span style={{ color: 'var(--text3)' }}>Buyer:</span> <b>{o.buyer}</b></div>
            <div><span style={{ color: 'var(--text3)' }}>Style:</span> <b>{o.style_name || '—'}</b></div>
            <div><span style={{ color: 'var(--text3)' }}>Factory:</span> <b>{o.factory || '—'}</b></div>
            <div><span style={{ color: 'var(--text3)' }}>Qty:</span> <b>{Number(o.orderqty || o.qty || 0).toLocaleString('en-IN')} pcs</b></div>
            <div><span style={{ color: 'var(--text3)' }}>Current Stage:</span> <span style={{ color: accent, fontWeight: 700 }}>{o.current_pending_stage || o.current_stage || '—'}</span></div>
          </div>
          <div style={{ fontSize: 14, color: 'var(--text3)' }}>
            {isOpen ? '▲' : '▼'}
          </div>
        </div>

        {/* Stages list inside TAS */}
        {isOpen && (
          <div style={{ padding: '14px 16px', background: 'var(--bg)' }}>
            {tData?.loading && (
              <div style={{ padding: '20px 10px', color: 'var(--text3)', fontSize: 12 }}>Loading planned stages for {o.tas_no}...</div>
            )}
            {tData?.error && (
              <div style={{ padding: '10px', color: '#ef4444', fontSize: 12 }}>Error: {tData.error}</div>
            )}
            {tData?.savedMsg && (
              <div style={{ padding: '6px 12px', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', borderRadius: 6, marginBottom: 10, fontSize: 12, fontWeight: 700 }}>
                ✓ {tData.savedMsg}
              </div>
            )}
            {tData?.stages && visibleStages.length === 0 && (
              <div style={{ padding: '14px 10px', color: 'var(--text3)', fontSize: 12, textAlign: 'center' }}>
                No applicable stages matching the filter ({statusFilter} {captainFilter !== 'ALL' ? `for ${captainFilter}` : ''}).
              </div>
            )}
            {tData?.stages && visibleStages.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10 }}>
                {visibleStages.map(st => {
                  const uid = st.uniqueid || st.id;
                  const isDone = st.status === 'done' || !!st.actual_date;
                  const isSaving = savingUid === uid;
                  const capBadge = CAPTAIN_BADGES[st.captain] || CAPTAIN_BADGES.MERCH;

                  return (
                    <div
                      key={uid}
                      style={{
                        padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)',
                        background: isDone ? 'rgba(16, 185, 129, 0.05)' : 'var(--bg2)',
                        display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 700, color: accent, fontSize: 11 }}>{st.sort_order}</span>
                          <span style={{ fontWeight: 700, color: 'var(--text)' }}>{st.stage_name}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <span style={{
                            fontSize: 9.5, fontWeight: 800, padding: '2px 5px', borderRadius: 4,
                            background: capBadge.bg, color: capBadge.text
                          }}>
                            {st.captain}
                          </span>
                          <span style={{
                            fontSize: 9.5, fontWeight: 700, padding: '2px 5px', borderRadius: 4,
                            background: isDone ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                            color: isDone ? '#10b981' : '#ef4444'
                          }}>
                            {isDone ? 'DONE' : 'PENDING'}
                          </span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text3)', fontSize: 11 }}>
                        <span>Est: <b style={{ color: 'var(--text)' }}>{fmtD(st.target_date || st.planned_date || st.date)}</b></span>
                        <span>Act: <b>{fmtD(st.actual_date)}</b></span>
                      </div>

                      <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
                        <input
                          type="date"
                          value={st.inputDate || ''}
                          onChange={e => setDateInput(o.tas_no, uid, e.target.value)}
                          style={{
                            flex: 1, padding: '5px 8px', borderRadius: 5, border: '1px solid var(--border)',
                            background: 'var(--bg)', color: 'var(--text)', fontSize: 11.5
                          }}
                        />
                        <button
                          onClick={() => saveStage(o.tas_no, st)}
                          disabled={isSaving}
                          style={{
                            padding: '5px 12px', borderRadius: 5, border: 'none',
                            background: isDone ? 'var(--bg3)' : accent,
                            color: isDone ? 'var(--text2)' : '#fff',
                            fontWeight: 700, fontSize: 11, cursor: 'pointer'
                          }}
                        >
                          {isSaving ? '...' : isDone ? 'Update' : 'Mark Done'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="screen">
      <Header subtitle="TAS · DAILY ACTIVITY · RW ROUTING" />

      {/* Header bar */}
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('tas-daily')} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 20, color: accent }}>
            📅 RW Team Manual Routing
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
            Strictly stages where <b>applicable = 1</b> &amp; <b>Estimated Date exists</b>, owned by RW Team Captains (<b>MERCH</b>, <b>FAB</b>, <b>LOG</b>, <b>COMM</b>).
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search TAS, Buyer, Style..."
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, minWidth: 260 }}
          />
        </div>
      </div>

      {/* Controls Bar: Grouping & Sorting + Captain & Status Filters */}
      <div style={{ padding: '10px 20px', background: 'var(--bg2)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {/* Group By Stage Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            onClick={() => setGroupByStage(g => !g)}
            style={{
              padding: '5px 12px', borderRadius: 14, fontSize: 11, cursor: 'pointer', fontWeight: 700,
              border: `1px solid ${groupByStage ? accent : 'var(--border)'}`,
              background: groupByStage ? `${accent}18` : 'transparent',
              color: groupByStage ? accent : 'var(--text3)'
            }}
          >
            {groupByStage ? '✓ Group by Stage: ON' : 'Group by Stage: OFF'}
          </button>

          {groupByStage && (
            <button
              onClick={() => setStageSortDir(d => d === 'DESC' ? 'ASC' : 'DESC')}
              title="Toggle Stage Sort Direction"
              style={{
                padding: '5px 10px', borderRadius: 14, fontSize: 11, cursor: 'pointer', fontWeight: 700,
                border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)'
              }}
            >
              {stageSortDir === 'DESC' ? '⬇ Last Stage First' : '⬆ First Stage First'}
            </button>
          )}
        </div>

        <div style={{ height: 18, width: 1, background: 'var(--border)' }} />

        {/* Filter By RW Captain */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase' }}>Captain:</span>
          {['ALL', 'MERCH', 'FAB', 'LOG', 'COMM'].map(c => {
            const b = CAPTAIN_BADGES[c] || { bg: 'var(--bg)', text: 'var(--text2)' };
            const active = captainFilter === c;
            return (
              <button
                key={c}
                onClick={() => setCaptainFilter(c)}
                style={{
                  padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  border: active ? `2px solid ${b.text}` : '1px solid var(--border)',
                  background: active ? b.bg : 'var(--bg)', color: active ? b.text : 'var(--text2)'
                }}
              >
                {c === 'ALL' ? 'ALL' : c}
              </button>
            );
          })}
        </div>

        {/* Stage Status filter (Pending vs Done vs All) */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)' }}>Status:</span>
          {['ALL', 'PENDING', 'DONE'].map(st => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              style={{
                padding: '3px 8px', borderRadius: 5, fontSize: 10.5, fontWeight: 700, cursor: 'pointer',
                border: statusFilter === st ? `1px solid ${accent}` : '1px solid var(--border)',
                background: statusFilter === st ? accent : 'var(--bg)', color: statusFilter === st ? '#fff' : 'var(--text3)'
              }}
            >
              {st}
            </button>
          ))}
        </div>
      </div>

      {/* Orders List */}
      <div style={{ padding: '16px 20px' }}>
        {loading && <div style={{ color: 'var(--text3)', padding: 20 }}>Loading TAS orders...</div>}
        {!loading && filteredOrders.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text3)' }}>No TAS orders found.</div>
        )}

        {/* Grouped by Stage View */}
        {!loading && groupByStage && (
          <div>
            {stageGroups.map(g => {
              const isCollapsed = !!collapsedStages[g.stage];
              return (
                <div key={g.stage} style={{ marginBottom: 18 }}>
                  {/* Stage Group Header Bar */}
                  <div
                    onClick={() => toggleStageCollapse(g.stage)}
                    style={{
                      padding: '10px 14px', borderRadius: 8, background: 'var(--bg3)',
                      border: '1px solid var(--border)', display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', cursor: 'pointer', marginBottom: isCollapsed ? 0 : 8
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 12, color: 'var(--text3)' }}>{isCollapsed ? '▶' : '▼'}</span>
                      <span style={{ fontWeight: 800, fontSize: 13, color: accent, letterSpacing: '0.3px' }}>
                        {g.stage}
                      </span>
                      <span style={{
                        fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                        background: `${accent}20`, color: accent
                      }}>
                        {g.orders.length} {g.orders.length === 1 ? 'Order' : 'Orders'}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 600 }}>
                      Total: <b style={{ color: 'var(--text)' }}>{g.totalQty.toLocaleString('en-IN')} pcs</b>
                    </div>
                  </div>

                  {/* Orders within Stage */}
                  {!isCollapsed && (
                    <div style={{ paddingLeft: 12, borderLeft: `2px solid ${accent}33` }}>
                      {g.orders.map(o => renderOrderCard(o))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Flat Un-grouped View */}
        {!loading && !groupByStage && (
          <div>
            {filteredOrders.map(o => renderOrderCard(o))}
          </div>
        )}
      </div>
    </div>
  );
}
